import React from 'react';
import { Input } from 'antd';
import type { InputRef } from 'antd';

type PhoneInputProps = {
  value?: string;
  onChange?: (v: string) => void;
  maxLength?: number;
  placeholder?: string;
};

export function PhoneNumberInput({
  value = '',
  onChange,
  maxLength = 10,
  placeholder = 'Please enter...',
}: PhoneInputProps) {
  const ref = React.useRef<InputRef>(null);

  const handleBeforeInput = (e: React.FormEvent<HTMLInputElement>) => {
    const ev = e.nativeEvent as InputEvent;
    if (ev.isComposing) return;
    const data = ev.data ?? '';
    if (data && /\D/.test(data)) {
      e.preventDefault();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const el = ref.current?.input;
    if (!el) return;

    const paste = e.clipboardData.getData('text').replace(/\D/g, '');
    const start = el.selectionStart ?? value.length;
    const end = el.selectionEnd ?? value.length;

    const next = (value.slice(0, start) + paste + value.slice(end)).slice(0, maxLength);

    onChange?.(next);

    requestAnimationFrame(() => {
      const pos = Math.min(start + paste.length, next.length);
      el.setSelectionRange(pos, pos);
    });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const onlyDigits = e.target.value.replace(/\D/g, '');
    if (onlyDigits !== e.target.value) {
      onChange?.(onlyDigits.slice(0, maxLength));
    } else {
      onChange?.(onlyDigits.slice(0, maxLength));
    }
  };

  return (
    <Input
      size='large'
      style={{ height: '48px', fontSize: '16px', fontWeight: '500', borderRadius: '4px' }}
      ref={ref}
      value={value}
      onChange={handleChange}
      onBeforeInput={handleBeforeInput}
      onPaste={handlePaste}
      placeholder={placeholder}
      maxLength={maxLength}
      inputMode='numeric'
      autoComplete='tel'
    />
  );
}

export default PhoneNumberInput;